import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  Code2,
  Copy,
  Film,
  Image as ImageIcon,
  Search,
  Sparkles,
  Volume2,
} from 'lucide-react';
import promptLibraryJSON from '../server/seed/prompt_library.json';
import { useLocationPath } from '../lib/router';

export interface PromptCategory {
  slug: string;
  name: string;
  description: string;
}

export interface LibraryPrompt {
  slug: string;
  title: string;
  summary: string;
  category: string;
  type: 'text' | 'image' | 'video' | 'audio';
  model: string;
  featured: boolean;
  tags: string[];
  prompt: string;
}

const categories = promptLibraryJSON.categories as PromptCategory[];
export const libraryPrompts = promptLibraryJSON.prompts as LibraryPrompt[];
export const libraryPromptBySlug = Object.fromEntries(libraryPrompts.map((item) => [item.slug, item]));

const typeMeta = {
  text: { name: 'Text', icon: Code2 },
  image: { name: 'Image', icon: ImageIcon },
  video: { name: 'Video', icon: Film },
  audio: { name: 'Audio', icon: Volume2 },
} as const;

function categoryBySlug(slug: string) {
  return categories.find((category) => category.slug === slug);
}

function tryHref(item: LibraryPrompt) {
  const params = new URLSearchParams({ model: item.model, prompt: item.prompt });
  if (item.type === 'text') return `/chat?${params.toString()}`;
  if (item.type === 'image') return `/text-to-image?${params.toString()}`;
  if (item.type === 'video') return `/spaces/seedance-2?${params.toString()}`;
  return `/music?${params.toString()}`;
}

function CopyButton({ text, compact = false }: { text: string; compact?: boolean }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard permission can be unavailable in prerendered previews.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`inline-flex items-center justify-center gap-1.5 rounded border border-white/12 text-white/60 transition-colors hover:border-white/30 hover:text-white ${compact ? 'px-2 py-1 font-mono text-[11px]' : 'h-11 px-4 font-mono text-sm'}`}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function PromptCard({ item }: { item: LibraryPrompt }) {
  const meta = typeMeta[item.type];
  const Icon = meta.icon;
  return (
    <article className="group flex h-full flex-col rounded-lg border border-white/10 bg-white/[0.025] p-5 transition-colors hover:border-white/24 hover:bg-white/[0.045]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded border border-white/10 bg-black/25 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
          <Icon className="h-3.5 w-3.5" /> {meta.name}
        </span>
        <span className="truncate font-mono text-[11px] text-white/30">{item.model}</span>
      </div>
      <a href={`/prompts/${item.slug}`} className="text-xl font-semibold tracking-tight text-white group-hover:underline">
        {item.title}
      </a>
      <p className="mt-2 flex-1 text-sm leading-6 text-white/52">{item.summary}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {item.tags.slice(0, 4).map((tag) => (
          <span key={tag} className="rounded bg-white/[0.04] px-2 py-1 font-mono text-[10px] text-white/38">#{tag}</span>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
        <CopyButton text={item.prompt} compact />
        <a href={`/prompts/${item.slug}`} className="inline-flex items-center gap-1.5 font-mono text-xs text-white/55 hover:text-white">
          View prompt <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </article>
  );
}

function PromptDetail({ item }: { item: LibraryPrompt }) {
  const category = categoryBySlug(item.category);
  const meta = typeMeta[item.type];
  const Icon = meta.icon;
  const related = libraryPrompts
    .filter((candidate) => candidate.slug !== item.slug && (candidate.category === item.category || candidate.type === item.type))
    .slice(0, 3);

  React.useEffect(() => window.scrollTo(0, 0), [item.slug]);

  return (
    <main className="px-4 pb-24 pt-28 sm:px-6">
      <article className="mx-auto max-w-4xl">
        <a href="/prompts" className="inline-flex items-center gap-1.5 text-sm font-medium text-white/45 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Prompt library
        </a>
        <header className="mt-5 max-w-3xl">
          <div className="mb-4 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.13em] text-white/42">
            <a href={`/prompts/category/${item.category}`} className="rounded border border-white/10 px-2 py-1 hover:border-white/25 hover:text-white">
              {category?.name || item.category}
            </a>
            <a href={`/prompts/type/${item.type}`} className="inline-flex items-center gap-1.5 rounded border border-white/10 px-2 py-1 hover:border-white/25 hover:text-white">
              <Icon className="h-3.5 w-3.5" /> {meta.name}
            </a>
            <span className="rounded border border-white/10 px-2 py-1 text-white/32">{item.model}</span>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">{item.title}</h1>
          <p className="mt-4 text-lg leading-7 text-white/58">{item.summary}</p>
        </header>

        <section className="mt-8 overflow-hidden rounded-lg border border-white/10 bg-white/[0.025]">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <span className="font-mono text-xs uppercase tracking-[0.16em] text-white/38">Copy-ready prompt</span>
            <CopyButton text={item.prompt} compact />
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap px-5 py-6 font-mono text-sm leading-7 text-white/78">{item.prompt}</pre>
        </section>

        <div className="mt-5 flex flex-wrap gap-3">
          <a href={tryHref(item)} className="inline-flex h-11 items-center gap-2 rounded bg-sky-400 px-4 font-mono text-sm font-bold text-black hover:bg-sky-300">
            Try with {item.model} <ArrowRight className="h-4 w-4" />
          </a>
          <CopyButton text={item.prompt} />
        </div>

        <div className="mt-6 flex flex-wrap gap-1.5">
          {item.tags.map((tag) => (
            <a key={tag} href={`/prompts?q=${encodeURIComponent(tag)}`} className="rounded bg-white/[0.04] px-2 py-1 font-mono text-xs text-white/42 hover:text-white/70">#{tag}</a>
          ))}
        </div>

        {related.length > 0 && (
          <section className="mt-14 border-t border-white/10 pt-8">
            <h2 className="mb-5 text-2xl font-semibold tracking-tight">Related prompts</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {related.map((candidate) => <PromptCard key={candidate.slug} item={candidate} />)}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}

function PromptIndex({ categorySlug = '', typeSlug = '' }: { categorySlug?: string; typeSlug?: string }) {
  const initialQuery = typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('q') || '';
  const [query, setQuery] = React.useState(initialQuery);
  const activeCategory = categoryBySlug(categorySlug);
  const activeType = typeSlug && typeSlug in typeMeta ? (typeSlug as keyof typeof typeMeta) : '';
  const filtered = React.useMemo(() => {
    const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return libraryPrompts.filter((item) => {
      if (categorySlug && item.category !== categorySlug) return false;
      if (activeType && item.type !== activeType) return false;
      if (!terms.length) return true;
      const haystack = `${item.title} ${item.summary} ${item.prompt} ${item.model} ${item.tags.join(' ')}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [activeType, categorySlug, query]);

  const heading = activeCategory?.name || (activeType ? `${typeMeta[activeType].name} prompts` : 'AI prompt library');
  const description = activeCategory?.description || (activeType
    ? `Copy-ready ${typeMeta[activeType].name.toLowerCase()} prompts for app.nz model routes and generation spaces.`
    : 'Prompts for agents, coding, research, images, video, audio, and product work. Copy one, then run it on the same platform.');

  return (
    <main className="mx-auto max-w-7xl px-6 pb-24 pt-28">
      <section className="max-w-4xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-xs uppercase tracking-[0.18em] text-white/45">
          <Sparkles className="h-3.5 w-3.5" /> Prompt library
        </div>
        <h1 className="text-4xl font-semibold tracking-tight md:text-6xl">{heading}</h1>
        <p className="mt-5 max-w-3xl text-lg leading-8 text-white/58">{description}</p>
        <div className="mt-6 flex flex-wrap gap-2 font-mono text-xs text-white/42">
          <span className="rounded border border-white/10 px-2 py-1">{libraryPrompts.length} maintained prompts</span>
          <span className="rounded border border-white/10 px-2 py-1">{categories.length} workflows</span>
          <span className="rounded border border-white/10 px-2 py-1">web · API · CLI · MCP</span>
        </div>
      </section>

      <section className="mt-10">
        <label className="relative block max-w-3xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by job, model, tag, or phrase…"
            className="h-14 w-full rounded-lg border border-white/12 bg-white/[0.035] pl-12 pr-4 text-base text-white outline-none placeholder:text-white/30 focus:border-white/30"
          />
        </label>

        <div className="mt-5 flex flex-wrap gap-2">
          <a href="/prompts" className={`rounded border px-3 py-1.5 font-mono text-xs ${!categorySlug && !activeType ? 'border-white bg-white text-black' : 'border-white/10 text-white/52 hover:border-white/25 hover:text-white'}`}>All</a>
          {categories.map((category) => (
            <a key={category.slug} href={`/prompts/category/${category.slug}`} className={`rounded border px-3 py-1.5 font-mono text-xs ${categorySlug === category.slug ? 'border-white bg-white text-black' : 'border-white/10 text-white/52 hover:border-white/25 hover:text-white'}`}>{category.name}</a>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(typeMeta).map(([slug, meta]) => {
            const Icon = meta.icon;
            return (
              <a key={slug} href={`/prompts/type/${slug}`} className={`inline-flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-xs ${activeType === slug ? 'border-sky-300 bg-sky-300 text-black' : 'border-white/10 text-white/52 hover:border-white/25 hover:text-white'}`}>
                <Icon className="h-3.5 w-3.5" /> {meta.name}
              </a>
            );
          })}
        </div>
      </section>

      <section className="mt-8">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-white/35">Library</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight">{filtered.length} {filtered.length === 1 ? 'prompt' : 'prompts'}</h2>
          </div>
          <a href="/mcp" className="hidden items-center gap-1.5 text-sm text-white/45 hover:text-white sm:inline-flex">
            <Bot className="h-4 w-4" /> Use from an agent
          </a>
        </div>
        {filtered.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => <PromptCard key={item.slug} item={item} />)}
          </div>
        ) : (
          <div className="rounded-lg border border-white/10 px-6 py-14 text-center font-mono text-sm text-white/40">No prompts match “{query}”.</div>
        )}
      </section>
    </main>
  );
}

export const PromptLibraryPage: React.FC<{ path?: string }> = ({ path: pathProp }) => {
  const path = useLocationPath(pathProp);
  const categoryMatch = path.match(/^\/prompts\/category\/([^/?#]+)/);
  const typeMatch = path.match(/^\/prompts\/type\/([^/?#]+)/);
  if (categoryMatch) return <PromptIndex categorySlug={decodeURIComponent(categoryMatch[1])} />;
  if (typeMatch) return <PromptIndex typeSlug={decodeURIComponent(typeMatch[1])} />;
  const detailMatch = path.match(/^\/prompts\/([^/?#]+)/);
  if (detailMatch) {
    const item = libraryPromptBySlug[decodeURIComponent(detailMatch[1])];
    if (item) return <PromptDetail item={item} />;
    return (
      <main className="px-6 pb-24 pt-32 text-center">
        <h1 className="text-3xl font-semibold">Prompt not found</h1>
        <a href="/prompts" className="mt-5 inline-flex items-center gap-2 text-sky-300 hover:underline"><ArrowLeft className="h-4 w-4" /> Prompt library</a>
      </main>
    );
  }
  return <PromptIndex />;
};

export default PromptLibraryPage;
